import React from 'react';

import {GenericHookPaginatedList} from 'app/components/general/lists/GenericHookPaginatedList';
import {
  useRemoveTeamItem,
  useTeams,
  useUpdateMemberAccess,
} from 'app/teams/api';
import {TeamItem} from 'app/teams/components/TeamItem';
import {TeamsFetchParamsType, TeamsLocationParamsType} from 'app/teams/types';
import {
  convertLocationParamsToFetchParams,
  getMembership,
} from 'app/teams/utils';
import {useUserDetails} from 'app/user/api';
import {gt} from 'app/utils/i18n';

import type {WithRouterProps} from 'react-router';
import type {TeamSerializedType, UserSerializedType} from 'sweet-types';

export function TeamList(routerProps: WithRouterProps) {
  const {data: user} = useUserDetails({id: routerProps.params.userid});
  return (
    <GenericHookPaginatedList<
      TeamSerializedType,
      TeamsLocationParamsType,
      TeamsFetchParamsType
    >
      {...routerProps}
      useDataHook={useTeams}
      hookParams={{
        user_id: routerProps.params.userid,
      }}
      convertParamsFunc={convertLocationParamsToFetchParams}
      renderItem={(team) => (
        <UserTeamItem team={team} user={user!} key={team.id} />
      )}
      messages={{
        notFound: ({NoResultsMessage}) => (
          <NoResultsMessage
            head={
              user?.is_guest_until != null
                ? gt('Guests cannot be team members')
                : gt('This member is not a member of any team.')
            }
            icon="fas fa-user-clock"
          />
        ),
      }}
    />
  );
}

interface UserTeamItemProps {
  team: TeamSerializedType;
  user: UserSerializedType;
}

const UserTeamItem = ({team, user}: UserTeamItemProps) => {
  const [removeTeamItem] = useRemoveTeamItem();
  const [updateMemberAccess] = useUpdateMemberAccess();
  const membership = getMembership(user, team);

  return (
    <TeamItem
      object={team}
      isManager={membership?.access == 'manager'}
      actionsGetter={(team: TeamSerializedType) => {
        const membership = getMembership(user!, team);
        const loadingIfHasNoMembership =
          membership == null
            ? {
                text: gt('Loading...'),
                icon: 'fa fa-circle-notch fa-spin',
                disabled: true,
              }
            : {};
        return [
          {
            text: gt('Team Manager'),
            icon: `far fa-${
              membership?.access == 'manager' ? 'check-' : ''
            }square`,
            key: 'manager',
            disabled: membership?.access == 'manager',
            condition: () => team.can_edit,
            callback: () =>
              updateMemberAccess({
                url: membership!.url,
                access: 'manager',
                userUrl: user.url,
              }),
          },
          {
            text: gt('Team Member'),
            key: 'teammember',
            icon: `far fa-${
              membership?.access == 'teammate' ? 'check-' : ''
            }square`,
            disabled: membership?.access == 'teammate',
            condition: () => team.can_edit,
            callback: () =>
              updateMemberAccess({
                url: membership!.url,
                access: 'teammate',
                userUrl: user.url,
              }),
          },
          {
            text: gt('Read Only Member'),
            key: 'reader',
            icon: `far fa-${
              membership?.access == 'reader' ? 'check-' : ''
            }square`,
            disabled: membership?.access == 'reader',
            condition: () => team.can_edit,
            callback: () =>
              updateMemberAccess({
                url: membership!.url,
                access: 'reader',
                userUrl: user.url,
              }),
          },
          {
            text: gt('Remove from Team'),
            key: 'remove',
            condition: () => team.can_edit,
            icon: 'fa fa-times',
            callback: () =>
              removeTeamItem({
                teamURL: team.url,
                item: user!,
              }),
            ...loadingIfHasNoMembership,
          },
        ];
      }}
    />
  );
};
